import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { securityService } from '../services/securityService';
import { AlertLevel } from '../types/security';
import {
  mapSeverityToLevel,
  getAlertLevelConfig,
  groupAlertsByLevel,
} from '../utils/alertMapping';

interface Alert {
  id: number;
  guest_id: number;
  guest_name: string;
  guest_photo?: string;
  hotel_name: string;
  severity: number;
  risk_factors: string[];
  status: string;
  created_at: string;
  acknowledged_at?: string;
}

export default function SecurityAlertsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<AlertLevel | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await securityService.getAlerts();
      const mappedAlerts = data.map((alert: any) => ({
        id: alert.id,
        guest_id: alert.guest_id || 0,
        guest_name: alert.guest_name || 'Unknown',
        guest_photo: alert.guest_photo,
        hotel_name: alert.hotel_name || 'Unknown Hotel',
        severity: alert.severity,
        risk_factors: alert.risk_factors || [],
        status: alert.status,
        created_at: alert.created_at,
        acknowledged_at: alert.acknowledged_at,
      }));
      setAlerts(mappedAlerts);
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.warn('Session expired — redirecting to login');
      } else {
        console.error('Error loading alerts:', error);
      }
    } finally {
      setLoading(false);
    }
  };


  const handleAcknowledge = async (alertId: number) => {
    try {
      await securityService.acknowledgeAlert(alertId.toString());
      loadAlerts();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const filteredAlerts = alerts.filter((alert) => {
    if (filter !== 'all' && alert.status !== filter) return false;
    if (severityFilter !== null && mapSeverityToLevel(alert.severity) !== severityFilter)
      return false;
    return true;
  });

  const grouped = groupAlertsByLevel(alerts);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Chargement des alertes...</div>
      </div>
    );
  }

  const douceConfig = getAlertLevelConfig('douce');
  const forteConfig = getAlertLevelConfig('forte');
  const rougeConfig = getAlertLevelConfig('rouge');

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🛡️ Alertes de Sécurité</h1>
          <p className="text-gray-600">Gestion centralisée des alertes de sécurité</p>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="all">Tous</option>
                <option value="active">Actif</option>
                <option value="acknowledged">Accusé réception</option>
                <option value="resolved">Résolu</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gravité</label>
              <select
                value={severityFilter ?? ''}
                onChange={(e) =>
                  setSeverityFilter(e.target.value ? (e.target.value as AlertLevel) : null)
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Toutes</option>
                <option value="rouge">{rougeConfig.emoji} {rougeConfig.label}</option>
                <option value="forte">{forteConfig.emoji} {forteConfig.label}</option>
                <option value="douce">{douceConfig.emoji} {douceConfig.label}</option>
              </select>
            </div>
          </div>
        </div>


        {/* Statistiques — 3 cartes (douce, forte, rouge) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={`${rougeConfig.bgColor} rounded-lg p-4 border ${rougeConfig.borderColor}`}>
            <div className={`${rougeConfig.color} text-2xl font-bold`}>{grouped.rouge}</div>
            <div className={`${rougeConfig.color} text-sm`}>{rougeConfig.emoji} {rougeConfig.label}</div>
          </div>
          <div className={`${forteConfig.bgColor} rounded-lg p-4 border ${forteConfig.borderColor}`}>
            <div className={`${forteConfig.color} text-2xl font-bold`}>{grouped.forte}</div>
            <div className={`${forteConfig.color} text-sm`}>{forteConfig.emoji} {forteConfig.label}</div>
          </div>
          <div className={`${douceConfig.bgColor} rounded-lg p-4 border ${douceConfig.borderColor}`}>
            <div className={`${douceConfig.color} text-2xl font-bold`}>{grouped.douce}</div>
            <div className={`${douceConfig.color} text-sm`}>{douceConfig.emoji} {douceConfig.label}</div>
          </div>
        </div>

        {/* Liste des alertes */}
        <div className="space-y-4">
          {filteredAlerts.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Aucune alerte trouvée
            </div>
          ) : (
            filteredAlerts.map((alert) => {
              const level = mapSeverityToLevel(alert.severity);
              const config = getAlertLevelConfig(level);
              return (
                <div
                  key={alert.id}
                  className={`bg-white rounded-lg shadow p-6 border-l-4 ${config.borderColor}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-3">
                        {alert.guest_photo && (
                          <img
                            src={alert.guest_photo}
                            alt={alert.guest_name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {alert.guest_name}
                          </h3>
                          <p className="text-sm text-gray-600">{alert.hotel_name}</p>
                        </div>
                      </div>

                      <div className="mb-3">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${config.bgColor} ${config.color} ${config.borderColor}`}
                        >
                          {config.emoji} {config.label}
                        </span>
                        <span
                          className={`ml-2 inline-block px-3 py-1 rounded-full text-sm ${
                            alert.status === 'active'
                              ? 'bg-red-100 text-red-800'
                              : alert.status === 'acknowledged'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {alert.status === 'active'
                            ? 'Actif'
                            : alert.status === 'acknowledged'
                            ? 'Accusé réception'
                            : 'Résolu'}
                        </span>
                      </div>

                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Facteurs de risque:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {alert.risk_factors.map((factor, index) => (
                            <span
                              key={index}
                              className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                            >
                              {factor}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="text-xs text-gray-500">
                        Créée le {new Date(alert.created_at).toLocaleString('fr-FR')}
                        {alert.acknowledged_at && (
                          <span className="ml-2">
                            • Accusée le{' '}
                            {new Date(alert.acknowledged_at).toLocaleString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={() => navigate(`/guests/${alert.guest_id}`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                      >
                        Voir détails
                      </button>
                      {alert.status === 'active' && (
                        <button
                          onClick={() => handleAcknowledge(alert.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                        >
                          Accuser réception
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}